/**
 * usePaginatedLeads — cursor-less offset pagination on /api/leads.
 *
 * Backend caps at 3000 rows per response (Cloudflare-safe). This hook fetches
 * the first page immediately, exposes `total` (via /leads/count), and a
 * `loadMore()` action that appends the next 3000-row page. No leads are ever
 * hidden — the user can progressively expand until `total` rows are loaded.
 *
 * Usage:
 *   const { leads, total, loadMore, hasMore, isLoadingMore, reload } =
 *     usePaginatedLeads(() => ({ source: "kraya_whatsapp", ...filterParams }));
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

const PAGE_SIZE = 500;

export default function usePaginatedLeads(buildBaseParamsFn, deps = []) {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const inflightRef = useRef(0);

  const paramsFor = (offset) => ({
    ...buildBaseParamsFn(),
    limit: PAGE_SIZE,
    offset,
  });

  const load = useCallback(async () => {
    const rid = ++inflightRef.current;
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        api.get("/leads", { params: paramsFor(0) }),
        api.get("/leads/count", { params: buildBaseParamsFn() }),
      ]);
      if (inflightRef.current !== rid) return;
      setLeads(listRes.data || []);
      setTotal(countRes.data?.total || 0);
    } finally {
      if (inflightRef.current === rid) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const loadMore = useCallback(async () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const res = await api.get("/leads", { params: paramsFor(leads.length) });
      setLeads((prev) => [...prev, ...(res.data || [])]);
    } finally {
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads.length, isLoadingMore]);

  useEffect(() => {
    load();
  }, [load]);

  const hasMore = leads.length < total;
  return { leads, setLeads, total, loading, isLoadingMore, hasMore,
           loadMore, reload: load };
}
